import { App, Button, Form, Input, Select, Space, Table, Tag } from 'antd';
import { Link2, Pencil, Plus, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crestHttp } from '../../api/client.js';
import { EmptyState, FancyModal } from '../../components/ui/index.js';
import { AccessPositionConfigurationModal } from './AccessPositionConfigurationModal.js';
import type {
  BusinessUnitNode,
  PositionRow,
  RoleCatalogRow,
  UserOption,
} from './access-center-types.js';

interface PositionFormValue {
  code: string;
  nameZh: string;
  businessUnitId: string;
  jobFamily?: string;
  jobLevel?: string;
}

function flattenUnits(nodes: BusinessUnitNode[]): Array<{ value: string; label: string }> {
  return nodes.flatMap((node) => {
    const id = node.businessunitid ?? node.id ?? node.key;
    return [
      ...(id ? [{ value: id, label: node.name ?? node.title ?? id }] : []),
      ...flattenUnits(node.children ?? []),
    ];
  });
}

export function AccessPositionsPanel() {
  const { t } = useTranslation('accessCenter');
  const { message } = App.useApp();
  const [form] = Form.useForm<PositionFormValue>();
  const [editForm] = Form.useForm<{ nameZh: string }>();
  const [rows, setRows] = useState<PositionRow[]>([]);
  const [roles, setRoles] = useState<RoleCatalogRow[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [units, setUnits] = useState<BusinessUnitNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<PositionRow | null>(null);
  const [bindingPosition, setBindingPosition] = useState<PositionRow | null>(null);
  const unitOptions = useMemo(() => flattenUnits(units), [units]);
  const unitNameById = useMemo(
    () => new Map(unitOptions.map((option) => [option.value, option.label])),
    [unitOptions],
  );

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      crestHttp.get<{ rows: PositionRow[] }>('/admin/access/positions', {
        params: { pageSize: 200 },
      }),
      crestHttp.get<{ rows: RoleCatalogRow[] }>('/admin/access/roles', {
        params: { pageSize: 500, status: 'active' },
      }),
      crestHttp.get<{ nodes?: BusinessUnitNode[]; rows?: BusinessUnitNode[] }>(
        '/admin/users/bu-tree',
      ),
      crestHttp.get<{ rows: UserOption[] }>('/admin/users', { params: { pageSize: 500 } }),
    ])
      .then(([positions, catalog, businessUnits, userRows]) => {
        setRows(positions.data.rows ?? []);
        setRoles(catalog.data.rows ?? []);
        setUnits(businessUnits.data.nodes ?? businessUnits.data.rows ?? []);
        setUsers(userRows.data.rows ?? []);
      })
      .catch(() => message.error(t('loadFailed')))
      .finally(() => setLoading(false));
  }, [message, t]);
  useEffect(() => load(), [load]);

  const createPosition = async () => {
    const values = await form.validateFields();
    await crestHttp.post('/admin/access/positions', values);
    message.success(t('created'));
    setCreateOpen(false);
    form.resetFields();
    load();
  };

  const openEditPosition = (position: PositionRow) => {
    setEditingPosition(position);
    editForm.setFieldsValue({ nameZh: position.name_zh });
  };

  const updatePositionName = async () => {
    if (!editingPosition) return;
    const values = await editForm.validateFields();
    await crestHttp.patch(`/admin/access/positions/${editingPosition.position_id}`, values);
    message.success(t('positionUpdated'));
    setEditingPosition(null);
    editForm.resetFields();
    load();
  };

  return (
    <div className="crest-list-card">
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
          {t('newPosition')}
        </Button>
        <Button icon={<RefreshCw size={14} />} onClick={load}>
          {t('common:refresh')}
        </Button>
      </Space>
      <Table
        rowKey="position_id"
        loading={loading}
        dataSource={rows}
        locale={{ emptyText: <EmptyState title={t('noPositions')} compact /> }}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        columns={[
          { title: t('positionCode'), dataIndex: 'code', width: 150 },
          { title: t('positionName'), dataIndex: 'name_zh' },
          {
            title: t('enterpriseOrg'),
            dataIndex: 'businessunitid',
            render: (value) => unitNameById.get(value) ?? value,
          },
          { title: t('jobFamily'), dataIndex: 'job_family', width: 140 },
          { title: t('jobLevel'), dataIndex: 'job_level', width: 100 },
          {
            title: t('common:status'),
            dataIndex: 'status',
            width: 100,
            render: (value) => <Tag color={value === 'active' ? 'green' : 'default'}>{value}</Tag>,
          },
          {
            title: t('actions'),
            width: 220,
            render: (_, row) => (
              <Space size={4}>
                <Button
                  type="link"
                  icon={<Pencil size={14} />}
                  onClick={() => openEditPosition(row)}
                >
                  {t('editPosition')}
                </Button>
                <Button
                  type="link"
                  icon={<Link2 size={14} />}
                  onClick={() => setBindingPosition(row)}
                >
                  {t('configureRoles')}
                </Button>
              </Space>
            ),
          },
        ]}
      />
      <FancyModal
        title={t('newPosition')}
        subtitle={t('positionSubtitle')}
        icon={<Plus size={18} />}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={createPosition}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="code" label={t('positionCode')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="nameZh" label={t('positionName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="businessUnitId" label={t('enterpriseOrg')} rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={unitOptions} />
          </Form.Item>
          <Space align="start" style={{ width: '100%' }}>
            <Form.Item name="jobFamily" label={t('jobFamily')}>
              <Input />
            </Form.Item>
            <Form.Item name="jobLevel" label={t('jobLevel')}>
              <Input />
            </Form.Item>
          </Space>
        </Form>
      </FancyModal>
      <FancyModal
        title={t('editPositionFor', { name: editingPosition?.name_zh ?? '' })}
        subtitle={t('editPositionSubtitle')}
        icon={<Pencil size={18} />}
        open={!!editingPosition}
        onCancel={() => {
          setEditingPosition(null);
          editForm.resetFields();
        }}
        onOk={updatePositionName}
        okText={t('savePositionName')}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="nameZh"
            label={t('positionName')}
            rules={[
              { required: true, whitespace: true, message: t('positionNameRequired') },
              { max: 160 },
            ]}
          >
            <Input autoFocus maxLength={160} showCount />
          </Form.Item>
        </Form>
      </FancyModal>
      <AccessPositionConfigurationModal
        position={bindingPosition}
        roles={roles}
        users={users}
        onClose={() => setBindingPosition(null)}
      />
    </div>
  );
}
